import BlockIcon from '@mui/icons-material/Block';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/**
 * Order detail-drawer footer (AUT-123): the force-cancel action + audit
 * note. The action itself is role + permission + audit gated server-side —
 * this is presentation only.
 *
 * `cancellable` (architect guardrail #3) hides the force-cancel button for
 * terminal states (completed / released / cancelled / expired) so the
 * admin never fires a guaranteed 409. The backend remains the authority.
 * When not cancellable, the footer collapses to just the audit note.
 */
export const OrderDetailFooter = ({
  cancellable,
  busy,
  onForceCancel,
}: {
  cancellable: boolean;
  busy: boolean;
  onForceCancel: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: '14px 24px',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {cancellable ? (
        <Button
          variant="contained"
          color="error"
          disabled={busy}
          onClick={onForceCancel}
          startIcon={<BlockIcon />}
        >
          {t('orders.actions.forceCancel')}
        </Button>
      ) : null}
      <Box
        sx={{
          ml: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 11,
          color: tokens.text.tertiary,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 12 }} />
        {t('orders.actions.auditNote')}
      </Box>
    </Box>
  );
};
